import _                                                   from 'underscore';
import React, { FC, useContext, useEffect, useState }      from 'react';
import { Button, Header, Icon, List, Menu, MenuItemProps } from 'semantic-ui-react';
import { Cell, Grid }                                      from 'styled-css-grid';

import { CorvalOrganization, CorvalProject, SemanticUIColor, Size } from '../../../../../types';
import { ProjectList }                                              from './ProjectList';
import { EmptyProjectList }                                         from './EmptyProjectList';
import { TextLink }                                                 from '../../../../components/display/TextLink';
import { StyledContainer }                                          from '../../../../containers';
import { useCurrentUser, useFeatureFlags, useProject }              from '../../../../../hooks';
import { useSystemSettings }                                        from "@src/hooks/useSystemSettings";
import { ProjectDashboardContext }                                  from "@src/views/pages/projects/overviews/project-dashboard/ProjectDashboardIndexPage";
import { runIfFunction }                                            from "@src/util";
import { ENTITY_ICONS, ORGANIZATIONAL_ROLES }                       from "@src/constants";
import { ItemWithIcon }                                             from '@src/views/components/display/ItemWithIcon';


interface Props {
    organization: Partial<CorvalOrganization>,
    projects: CorvalProject[],
}

const defaultProps: Props = {
    organization: null,
    projects:     [],
};

const ProjectsByOrganizationListItem: FC<Props> = ( props ) => {

    const { compareProjects, showCreateProjectFromTemplateDialog } = useProject(),
          { isSystemAdmin, hasOrganizationalRole }                 = useCurrentUser(),
          { getVisibleOrgIds }                                     = useContext( ProjectDashboardContext ),
          featureFlags                                             = useFeatureFlags(),
          { filterProjectsByVisibility }                           = useSystemSettings();

    const visibleOrgs = runIfFunction( getVisibleOrgIds.current ) ?? [];

    const { organization, projects } = props,
          _index                     = _.findIndex( visibleOrgs, ( id ) => ( id === organization.id ) ),
          index                      = ( _index === -1 ? null : _index );

    enum PROJECT_VIEWS {
        MANAGED_PROJECTS = 'managed-projects',
        TEAM_PROJECTS    = 'team-projects',
        TEMPLATES        = 'templates'
    }

    const [ templateSourceProjects, nonTemplateSourceProjects ] = _.partition( projects, ( project ) => project?.isTemplateSource ),
          [ corvalManagedProjects, teamManagedProjects ]        = _.partition( nonTemplateSourceProjects, ( project ) => project?.isCorvalManaged ),
          [ projectView, setProjectView ]                       = useState<PROJECT_VIEWS>( _.isEmpty(nonTemplateSourceProjects) ? PROJECT_VIEWS.TEMPLATES : PROJECT_VIEWS.TEAM_PROJECTS ),
          [ projectViewData, setProjectViewData ]               = useState<CorvalProject[]>( teamManagedProjects );

    const hasProjects                      = ( projectViewData.length > 0 ),
          hasMultipleProjects              = ( projectViewData.length > 1 ),
          hasTeamManagedProjects           = ( teamManagedProjects.length > 0 ),
          hasTemplateProjects              = ( templateSourceProjects.length > 0 ),
          hasCorvalManagedProjects         = ( corvalManagedProjects.length > 0 ),
          hasMultipleCorvalManagedProjects = ( corvalManagedProjects.length > 1 ),
          showLinks                        = ( !_.isEmpty( visibleOrgs ) && visibleOrgs.length > 1 ),
          hasIndex                         = !_.isNull( index ),
          includeNextIndex                 = hasIndex ? ( ( index + 1 ) < visibleOrgs.length ) : false,
          indexIsNotZero                   = ( index !== 0 ),
          containerElID                    = `org-${ organization.id }`;

    const showCompareButton       = featureFlags.enableProjectCompare,
          showNewProjectButton    = featureFlags.enableProjectCreateFromTemplate,
          canCreateNewOrgProjects = isSystemAdmin() || hasOrganizationalRole( organization.id, ORGANIZATIONAL_ROLES.ADMIN );

    /* Project type view handling */
    useEffect( () => {

        if( projectView === PROJECT_VIEWS.MANAGED_PROJECTS ){

            setProjectViewData( corvalManagedProjects );

        } else if( projectView === PROJECT_VIEWS.TEAM_PROJECTS ){

            setProjectViewData( teamManagedProjects );

        } else if( projectView === PROJECT_VIEWS.TEMPLATES ){

            setProjectViewData( templateSourceProjects );

        } else {

            setProjectViewData( projects );
        }

    }, [ projectView ] );

    // Org Navigation Link Components
    const PreviousLink: FC = () => (
        <><Icon size={ 'large' } name={ 'arrow alternate circle up outline' }/> Prev Organization</>
    );

    const NextLink: FC = () => (
        <><Icon size={ 'large' } name={ 'arrow alternate circle down outline' }/> Next Organization</>
    );

    const MutedLink: FC = ( props ) => (
        <TextLink as={ 'span' } href={ `#org-${ index }` } className={ 'text--colored-dk-screen-25' }>{ props.children }</TextLink>
    );

    const handleCompareProjects = () => {
        compareProjects( _.pluck( corvalManagedProjects, 'id' ) );
    };

    const handleNewProject = () => {
        showCreateProjectFromTemplateDialog( () => {} );
    };

    const [ visibleProjects, noVisibleProjects ] = filterProjectsByVisibility( projects );

    if( noVisibleProjects ) return null;


    const handleProjectViewChange: MenuItemProps['onClick'] = ( e, data ) => {

        setProjectView( e.currentTarget.dataset.view as PROJECT_VIEWS );
    };

    return (

        <StyledContainer id={ containerElID } padding={ [ Size.LG, null ] }>

            <Grid columns={ `auto 1fr auto` } rowGap={ `20px` } columnGap={ '50px' }>

                <Cell middle width={ 2 }>

                    <Header as={ 'h1' } color={ SemanticUIColor.PURPLE }>{ organization.name }</Header>

                </Cell>
                <Cell>
                    <List horizontal>
                        { ( showCompareButton && hasMultipleProjects && hasMultipleCorvalManagedProjects ) && (
                            <List.Item>
                                <Button
                                    circular
                                    color={ 'purple' }
                                    icon={ 'chart bar outline' }
                                    onClick={ handleCompareProjects }
                                    content={ 'Compare Managed Projects' }
                                />
                            </List.Item>
                        ) }
                        { ( showNewProjectButton && canCreateNewOrgProjects ) && (
                            <List.Item>
                                <Button
                                    circular
                                    color={ 'purple' }
                                    icon={ ENTITY_ICONS.PROJECTS.ENTITY }
                                    onClick={ handleNewProject }
                                    content={ 'New Project' }
                                />
                            </List.Item>
                        ) }
                    </List>
                </Cell>

                { ( hasTeamManagedProjects || hasTemplateProjects || hasCorvalManagedProjects ) && (

                    <Cell middle width={ 3 }>

                        <Menu pointing secondary color={ SemanticUIColor.PURPLE }>

                            { hasCorvalManagedProjects && (
                                <Menu.Item onClick={ handleProjectViewChange } data-view={ PROJECT_VIEWS.MANAGED_PROJECTS } active={ ( projectView === PROJECT_VIEWS.MANAGED_PROJECTS ) }>
                                    <ItemWithIcon iconComponent={ <Icon name={ ENTITY_ICONS.PROJECTS.ENTITY }/> }>{ hasCorvalManagedProjects ? 'Managed Projects' : 'Projects' }</ItemWithIcon>
                                </Menu.Item>
                            ) }

                            { hasTeamManagedProjects && (
                                <Menu.Item onClick={ handleProjectViewChange } data-view={ PROJECT_VIEWS.TEAM_PROJECTS } active={ ( projectView === PROJECT_VIEWS.TEAM_PROJECTS ) }>
                                    <ItemWithIcon iconComponent={ <Icon name={ ENTITY_ICONS.PROJECTS.ENTITY }/> }>{ hasCorvalManagedProjects ? 'Team Projects' : 'Projects' }</ItemWithIcon>
                                </Menu.Item>
                            ) }

                            { hasTemplateProjects && (
                                <Menu.Item onClick={ handleProjectViewChange } data-view={ PROJECT_VIEWS.TEMPLATES } active={ ( projectView === PROJECT_VIEWS.TEMPLATES ) }>
                                    <ItemWithIcon iconComponent={ <Icon name={ ENTITY_ICONS.PROJECTS.TEMPLATES.ENTITY }/> }>Templates</ItemWithIcon>
                                </Menu.Item>
                            ) }

                        </Menu>

                    </Cell>
                ) }

                { showLinks && (
                    <Cell middle width={ 3 }>

                        <List horizontal divided relaxed>

                            <List.Item>

                                { indexIsNotZero
                                  ? <TextLink href={ `#org-${ visibleOrgs[ index - 1 ] }` }><PreviousLink/></TextLink>
                                  : <MutedLink><PreviousLink/></MutedLink>
                                }
                            </List.Item>
                            <List.Item>

                                { includeNextIndex
                                  ? <TextLink href={ `#org-${ visibleOrgs[ index + 1 ] }` }><NextLink/></TextLink>
                                  : <MutedLink><NextLink/></MutedLink>
                                }

                            </List.Item>

                        </List>
                    </Cell>
                ) }

                <Cell width={ 3 }>

                    { hasProjects
                      ? <ProjectList items={ projectViewData }/>
                      : <EmptyProjectList/>
                    }

                </Cell>
            </Grid>

        </StyledContainer>
    )
        ;
};

ProjectsByOrganizationListItem.defaultProps = defaultProps;

export { ProjectsByOrganizationListItem };
